import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, concatMap, filter, map, of, switchMap, tap, withLatestFrom } from "rxjs";
import { Store } from "@ngrx/store";
import { BatchItemOperationResult, CatalogClient, ItemImportResult, ItemOperationResult, ItemType, Operation, QuoteItemClient, SubscriptionCatalogStatus } from "src/api-service";
import { ArticlesActions, ProductCatalogActions } from "./articles.actions";
import { selectVersionItemCount, selectSelectedProductGroupIds } from "./articles.selectors";
import { CLIPBOARD_STORAGE_KEY } from "../../../application/clipboard/clipboard.state";
import { ClipboardActions } from "../../../application/clipboard/clipboard.actions";
import { normalizeError } from "../../../app-store-error.types";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { selectCountryProjectManagement } from "../../../application/data/country/countries/countries.selectors";
import { QuoteVersionActions } from "../version/version.actions";
import { selectVersion } from "../version/version.selectors";
import { selectIsSubscriptionQuote } from "../quote.selectors";

@Injectable()
export class ArticlesEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly quoteItemClient = inject(QuoteItemClient);
    private readonly catalogClient = inject(CatalogClient);

    // ******** CREATE ARTICLE ******** //
    createArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.createArticle),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, request }, selectedCompany]) =>
                this.quoteItemClient.createItem(selectedCompany.id, versionId, request).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.createArticleSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.CREATE_SUCCESS' })
                        : ArticlesActions.createArticleFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.CREATE_ERROR' })
                    ),
                    catchError(error => {
                        let response: ItemOperationResult;
                        if (error?.status === 400) {
                            response = extractOperationResultFromError(error);
                        } else if (error?.status === 500) {
                            response = createServerErrorResponse();
                        } else {
                            response = createNetworkErrorResponse();
                        }
                        return of(ArticlesActions.createArticleFailure({
                            response,
                            toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.CREATE_ERROR',
                        }));
                    }),
                ),
            ),
        ),
    );

    // ******** BATCH CREATE ARTICLES ******** //
    batchCreateArticles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.batchCreateArticles),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, request }, selectedCompany]) =>
                this.quoteItemClient.batchCreateItems(selectedCompany.id, versionId, request).pipe(
                    map(result => result.successCount > 0
                        ? ArticlesActions.batchCreateArticlesSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.BATCH_CREATE_SUCCESS' })
                        : ArticlesActions.batchCreateArticlesFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.BATCH_CREATE_ERROR' })
                    ),
                    catchError(error => {
                        const response = extractBatchOperationResultFromError(error);
                        return of(ArticlesActions.batchCreateArticlesFailure({ response, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.BATCH_CREATE_ERROR' }));
                    }),
                ),
            ),
        ),
    );

    // ******** BATCH CHANGE PRODUCT CATALOG ITEMS ******** //
    batchChangeCatalogProducts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.batchChangeCatalogProducts),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, request }, selectedCompany]) =>
                this.quoteItemClient.batchChangeCatalogProducts(selectedCompany.id, versionId, request).pipe(
                    map(result => result.successCount > 0
                        ? ArticlesActions.batchChangeCatalogProductsSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.BATCH_CHANGE_SUCCESS' })
                        : ArticlesActions.batchChangeCatalogProductsFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.BATCH_CHANGE_ERROR' })
                    ),
                    catchError(error => {
                        const response = extractBatchOperationResultFromError(error);
                        return of(ArticlesActions.batchChangeCatalogProductsFailure({ response, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.BATCH_CHANGE_ERROR' }));
                    }),
                ),
            ),
        ),
    );

    // ******** UPDATE ARTICLE FIELD ******** //
    updateArticleField$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.updateArticleField),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, request }, selectedCompany]) =>
                this.quoteItemClient.updateField(selectedCompany.id, versionId, request).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.updateArticleFieldSuccess({ response: result })
                        : ArticlesActions.updateArticleFieldFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.SAVE_ERROR' })
                    ),
                    catchError(error => of(ArticlesActions.updateArticleFieldFailure({
                        response: pickTransportErrorResponse(error, request.itemType, request.id, request.fieldName),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.SAVE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** DELETE ARTICLE ******** //
    deleteArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.deleteArticle),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, request }, selectedCompany]) =>
                this.quoteItemClient.deleteItem(selectedCompany.id, versionId, request).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.deleteArticleSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.DELETE_SUCCESS' })
                        : ArticlesActions.deleteArticleFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.DELETE_ERROR' })
                    ),
                    catchError(error => of(ArticlesActions.deleteArticleFailure({
                        response: pickTransportErrorResponse(error),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.DELETE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** BATCH DELETE ARTICLES ******** //
    batchDeleteArticles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.batchDeleteArticles),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, request }, selectedCompany]) =>
                this.quoteItemClient.batchDeleteItems(selectedCompany.id, versionId, request).pipe(
                    map(result => result.successCount > 0
                        ? ArticlesActions.batchDeleteArticlesSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.BATCH_DELETE_SUCCESS' })
                        : ArticlesActions.batchDeleteArticlesFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.BATCH_DELETE_ERROR' })
                    ),
                    catchError(error => {
                        const response = extractBatchOperationResultFromError(error);
                        return of(ArticlesActions.batchDeleteArticlesFailure({ response, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.BATCH_DELETE_ERROR' }));
                    }),
                ),
            ),
        ),
    );

    // ******** REFRESH ARTICLES ******** //
    refreshArticles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.refreshArticles),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId }, selectedCompany]) =>
                this.quoteItemClient.refresh(selectedCompany.id, versionId).pipe(
                    map(result => ArticlesActions.refreshArticlesSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.REFRESH_SUCCESS' })),
                    catchError(error => of(ArticlesActions.refreshArticlesFailure({
                        response: pickTransportErrorResponse(error),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.REFRESH_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** DUPLICATE ARTICLE ******** //
    duplicateArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.duplicateArticle),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, articleId }, selectedCompany]) =>
                this.quoteItemClient.duplicateItem(selectedCompany.id, versionId, articleId).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.duplicateArticleSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.DUPLICATE_ITEM_SUCCESS' })
                        : ArticlesActions.duplicateArticleFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.DUPLICATE_ITEM_ERROR' })
                    ),
                    catchError(error => of(ArticlesActions.duplicateArticleFailure({
                        response: pickTransportErrorResponse(error),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.DUPLICATE_ITEM_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** DUPLICATE ARTICLE GROUP ******** //
    duplicateArticleGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.duplicateArticleGroup),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, articleGroupId }, selectedCompany]) =>
                this.quoteItemClient.duplicateItemGroup(selectedCompany.id, versionId, articleGroupId).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.duplicateArticleGroupSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.DUPLICATE_GROUP_SUCCESS' })
                        : ArticlesActions.duplicateArticleGroupFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.DUPLICATE_GROUP_ERROR' })
                    ),
                    catchError(error => of(ArticlesActions.duplicateArticleGroupFailure({
                        response: pickTransportErrorResponse(error),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.DUPLICATE_GROUP_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** PASTE ARTICLE ******** //
    pasteArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.pasteArticle),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, request }, selectedCompany]) =>
                this.quoteItemClient.pasteItem(selectedCompany.id, versionId, request).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.pasteArticleSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.PASTE_SUCCESS' })
                        : ArticlesActions.pasteArticleFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.PASTE_ERROR' })
                    ),
                    catchError(error => of(ArticlesActions.pasteArticleFailure({
                        response: pickTransportErrorResponse(error),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.PASTE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** CLEAR CLIPBOARD AFTER PASTE ******** //
    clearClipboardAfterPaste$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.pasteArticleSuccess),
            tap(() => localStorage.removeItem(CLIPBOARD_STORAGE_KEY)),
            map(() => ClipboardActions.update({ data: null }))
        ),
    );

    // ******** MOVE ARTICLE ******** //
    // Unified endpoint: handles Item, ItemGroup, SubItem, AlternativeItem
    moveArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.moveArticle),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, request }, selectedCompany]) =>
                this.quoteItemClient.moveItem(selectedCompany.id, versionId, request).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.moveArticleSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.MOVE_SUCCESS' })
                        : ArticlesActions.moveArticleFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.MOVE_ERROR' })
                    ),
                    catchError(error => of(ArticlesActions.moveArticleFailure({
                        response: pickTransportErrorResponse(error),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.MOVE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** UPLOAD ARTICLE IMAGE ******** //
    uploadArticleImage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.uploadArticleImage),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, articleId, file }, selectedCompany]) =>
                this.quoteItemClient.uploadItemImage(
                    selectedCompany.id,
                    versionId,
                    articleId,
                    { data: file, fileName: file.name }
                ).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.uploadArticleImageSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.IMAGE_UPLOAD_SUCCESS' })
                        : ArticlesActions.uploadArticleImageFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.IMAGE_UPLOAD_ERROR' })
                    ),
                    catchError(error => of(ArticlesActions.uploadArticleImageFailure({
                        response: pickTransportErrorResponse(error),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.IMAGE_UPLOAD_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** CONVERT ARTICLE ******** //
    convertArticle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.convertArticle),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, request }, selectedCompany]) =>
                this.quoteItemClient.convertItem(selectedCompany.id, versionId, request).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.convertArticleSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.CONVERT_SUCCESS' })
                        : ArticlesActions.convertArticleFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.CONVERT_ERROR' })
                    ),
                    catchError(error => of(ArticlesActions.convertArticleFailure({
                        response: pickTransportErrorResponse(error),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.CONVERT_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** IMPORT ARTICLES FROM EXCEL ******** //
    importArticles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.importArticles),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ versionId, articleGroupId, file }, selectedCompany]) =>
                this.quoteItemClient.importItems(
                    selectedCompany.id,
                    versionId,
                    articleGroupId,
                    { data: file, fileName: file.name }
                ).pipe(
                    map(result => result.latestOperation.wasSuccessful
                        ? ArticlesActions.importArticlesSuccess({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.IMPORT_SUCCESS' })
                        : ArticlesActions.importArticlesFailure({ response: result, toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.IMPORT_ERROR' })
                    ),
                    catchError(error => of(ArticlesActions.importArticlesFailure({
                        response: createImportTransportError(error),
                        toastKey: 'QUOTE.DETAIL.ITEMS.TOAST.IMPORT_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** AUTO-TRIGGER PM RECALCULATION ON THRESHOLD CROSS ******** //
    triggerProjectManagementRecalculation$ = createEffect(() => {
        const tierByVersion = new Map<number, number>();
        return this.store.select(selectVersion).pipe(
            filter(version => !!version),
            withLatestFrom(
                this.store.select(selectCountryProjectManagement),
                this.store.select(selectVersionItemCount),
            ),
            filter(([version, countryPm, itemCount]) => {
                if (!countryPm) return false;
                if (!version.projectManagementAndKickback?.includeProjectManagementCost) return false;
                if (!version.projectManagementAndKickback?.projectManagementAutomaticCalculation) return false;

                const thresholds = [countryPm.itemThreshold1, countryPm.itemThreshold2, countryPm.itemThreshold3, countryPm.itemThreshold4, countryPm.itemThreshold5];
                const getTier = (count: number) => {
                    if (count < 1) return 0;
                    for (let i = 0; i < thresholds.length; i++) {
                        if (count <= thresholds[i]) return i + 1;
                    }
                    return 6;
                };

                const currentTier = getTier(itemCount);
                const previousTier = tierByVersion.get(version.id);
                tierByVersion.set(version.id, currentTier);

                if (previousTier === undefined) return false;

                return previousTier !== currentTier;
            }),
            map(([version]) => {
                const operation: Operation = {
                    op: "replace",
                    path: "/automaticRecalculateProjectManagement",
                    value: true,
                };
                return QuoteVersionActions.updateManagementAutoCalculation({
                    versionId: version.id,
                    toastKey: 'QUOTE.DETAIL.PROJECT_MANAGEMENT.THRESHOLD_CROSSED',
                    request: [operation],
                });
            }),
        );
    });

    // =========================================================================
    // SUBSCRIPTION QUOTE - Group/Article Selection Effects
    // =========================================================================

    clearProductGroupOnVersionChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.changeVersion),
            withLatestFrom(this.store.select(selectIsSubscriptionQuote)),
            filter(([_, isSubscription]) => isSubscription),
            map(() => ArticlesActions.selectProductGroups({ productGroupIds: [] })),
        ),
    );

    autoSelectNewGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.createArticleSuccess),
            filter(({ response }) => !!response.createdItemGroup),
            withLatestFrom(
                this.store.select(selectIsSubscriptionQuote),
                this.store.select(selectSelectedProductGroupIds),
            ),
            filter(([_, isSubscription]) => isSubscription),
            map(([{ response }, _, currentSelection]) => {
                const newGroupId = response.createdItemGroup!.id;
                return ArticlesActions.selectProductGroups({
                    productGroupIds: [...currentSelection, newGroupId],
                });
            }),
        ),
    );

    removeDeletedGroupFromSelection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.deleteArticleSuccess),
            withLatestFrom(
                this.store.select(selectIsSubscriptionQuote),
                this.store.select(selectSelectedProductGroupIds),
            ),
            filter(([_, isSubscription]) => isSubscription),
            map(([{ response }, _, currentSelection]) => {
                const existingGroupIds = new Set(response.itemGroups?.map(g => g.id) ?? []);
                const updatedSelection = currentSelection.filter(id => existingGroupIds.has(id));
                return ArticlesActions.selectProductGroups({
                    productGroupIds: updatedSelection,
                });
            }),
        ),
    );

    clearSelectedArticlesOnBatchChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ArticlesActions.batchChangeCatalogProductsSuccess),
            map(() => ArticlesActions.selectArticles({ articleIds: [] })),
        ),
    );

    // =========================================================================
    // PRODUCT CATALOG EFFECTS
    // =========================================================================

    loadProductCatalogs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProductCatalogActions.load),
            switchMap(({ companyId, quoteId, versionId }) =>
                this.catalogClient.getCatalogsForQuote(companyId, quoteId, versionId).pipe(
                    tap(result => {
                        if (result?.subscriptionStatus === SubscriptionCatalogStatus.NoDefaultFinancingCompany) {
                            this.store.dispatch(ProductCatalogActions.blocked({
                                toastKey: "SUBSCRIPTION.CATALOG.ERR_NO_DEFAULT_COMPANY",
                            }));
                        } else if (result?.subscriptionStatus === SubscriptionCatalogStatus.InterestRateExpired) {
                            this.store.dispatch(ProductCatalogActions.blocked({
                                toastKey: "SUBSCRIPTION.CATALOG.ERR_INTEREST_RATE_EXPIRED",
                            }));
                        }
                    }),
                    map(result => ProductCatalogActions.loadSuccess({ result })),
                    catchError(error => {
                        console.error("Failed to fetch product catalogs:", error);
                        return of(ProductCatalogActions.loadFailure({
                            error: normalizeError(error),
                            toastKey: "QUOTE.TOAST.LOAD_CATALOGS_ERROR",
                        }));
                    }),
                ),
            ),
        ),
    );

}

/**
 * NSwag has two distinct error paths:
 *   1. Typed body declared in spec → throws the parsed result object directly (our Item endpoints).
 *   2. Untyped status / parse failure → throws ApiException with `response: string`.
 * `isItemOperationResultLike` covers (1); `tryParseApiExceptionBody` covers (2).
 */
function isItemOperationResultLike(error: any): error is ItemOperationResult {
    return error != null
        && typeof error === 'object'
        && error.latestOperation != null
        && typeof error.latestOperation === 'object'
        && typeof error.latestOperation.wasSuccessful === 'boolean';
}

function tryParseApiExceptionBody<T extends ItemOperationResult>(error: any): T | null {
    const raw = error?.response;
    if (typeof raw !== 'string' || raw.length === 0) return null;
    try {
        const parsed = JSON.parse(raw) as T;
        return parsed?.latestOperation ? parsed : null;
    } catch {
        return null;
    }
}

function extractOperationResultFromError(error: any): ItemOperationResult {
    if (isItemOperationResultLike(error)) return error;
    return tryParseApiExceptionBody<ItemOperationResult>(error) ?? createServerErrorResponse();
}

function createOperationErrorResponse(
    errorCode: string,
    itemType: ItemType = ItemType.Item,
    id: number = 0,
    fieldName?: string,
): ItemOperationResult {
    return {
        latestOperation: {
            itemType,
            id,
            fieldName,
            wasSuccessful: false,
            errorCode,
            changedFields: [],
        },
    };
}

function createServerErrorResponse(
    itemType: ItemType = ItemType.Item,
    id: number = 0,
    fieldName?: string,
): ItemOperationResult {
    return createOperationErrorResponse('ERRORS.SERVER_ERROR', itemType, id, fieldName);
}

function createNetworkErrorResponse(
    itemType: ItemType = ItemType.Item,
    id: number = 0,
    fieldName?: string,
): ItemOperationResult {
    return createOperationErrorResponse('ERRORS.NETWORK_ERROR', itemType, id, fieldName);
}

function pickTransportErrorResponse(
    error: any,
    itemType: ItemType = ItemType.Item,
    id: number = 0,
    fieldName?: string,
): ItemOperationResult {
    if (isItemOperationResultLike(error)) return error;
    // status >= 100 means the server responded with something we just can't parse — that's
    // a server error, not a transport failure. status 0/undefined means the request never
    // reached the backend (CORS, offline, mid-flight restart) — that's a network error.
    return typeof error?.status === 'number' && error.status >= 100
        ? createServerErrorResponse(itemType, id, fieldName)
        : createNetworkErrorResponse(itemType, id, fieldName);
}

function createImportTransportError(error: any): ItemImportResult {
    return {
        ...pickTransportErrorResponse(error),
        importedCount: 0,
        skippedCount: 0,
        rowErrors: [],
    };
}

function extractBatchOperationResultFromError(error: any): BatchItemOperationResult {
    const direct = isItemOperationResultLike(error) && Array.isArray((error as any).results)
        ? error as BatchItemOperationResult
        : null;
    const parsed = direct ?? tryParseApiExceptionBody<BatchItemOperationResult>(error);

    if (parsed) {
        // Backend sets only WasSuccessful on the batch-level latestOperation; per-item errors
        // live on results[i]. Hoist the first failure so the generic toast handler shows it.
        if (!parsed.latestOperation.errorCode) {
            const firstFailure = parsed.results?.find(r => !r.wasSuccessful);
            if (firstFailure?.errorCode) {
                parsed.latestOperation.errorCode = firstFailure.errorCode;
                parsed.latestOperation.errorMessage = firstFailure.errorMessage;
            }
        }
        return parsed;
    }

    return {
        latestOperation: {
            itemType: ItemType.Item,
            id: 0,
            wasSuccessful: false,
            errorCode: typeof error?.status === 'number' && error.status >= 100
                ? 'ERRORS.SERVER_ERROR'
                : 'ERRORS.NETWORK_ERROR',
            changedFields: [],
        },
        results: [],
        successCount: 0,
        failedCount: 0,
    };
}

